// ─── ROUTE REGISTRY ──────────────────────────────────────────────────────────
// Single source of truth: routes, SEO meta, sidebar groups, tool mapping

export const TOOL_GROUPS = [
  {
    group: "JSON",
    color: "#3b8ef8",
    tools: [
      {
        id:    "json-toolkit",
        path:  "/json-toolkit",
        name:  "JSON Toolkit",
        icon:  "{ }",
        desc:  "Formatter, validator, CSV/XML/YAML, minifier, diff",
        title: "JSON Formatter, Beautifier & Converter — JSON to CSV, XML, YAML Free | miniDevToolkit",
        meta:  "Free JSON formatter, beautifier and validator. Convert JSON to CSV, XML, YAML. Minify, diff, and compare JSON online — 7 tools in one. No sign-up, no uploads, 100% private.",
        keywords: "json formatter online, json beautifier, beautify json online, json validator online, json to csv converter, json to xml converter, json to yaml converter, json minifier online, json diff checker, json compare online, format json online, validate json syntax, pretty print json, json tools free",
        faqs: [
          { q: "Is the JSON formatter free to use?", a: "Yes. The JSON Toolkit is completely free with no sign-up required. All formatting, validation, and conversion runs entirely in your browser." },
          { q: "Can I convert JSON to CSV online for free?", a: "Yes. The JSON to CSV tool converts any JSON array to a downloadable CSV file with column filtering, delimiter options, and header control — free, private, and no uploads." },
          { q: "Does the JSON formatter send my data to a server?", a: "No. Every operation — formatting, validation, conversion, minification, diff — runs 100% in your browser using JavaScript. Your JSON never leaves your device." },
        ],
      },
    ],
  },
  {
    group: "Encode / Decode",
    color: "#22c55e",
    tools: [
      {
        id:    "encoding",
        path:  "/encoding-toolkit",
        name:  "Encoding Toolkit",
        icon:  "🔐",
        desc:  "Base64, URL, HTML, JWT, Hex, Unicode, Morse",
        title: "Base64 Encoder & Decoder — JWT Decoder, URL Encode, Hex & Unicode Free | miniDevToolkit",
        meta:  "Free online encoding tools: Base64 encode/decode, URL encode/decode, HTML entity encoder, JWT token decoder with expiry check, Hex, Unicode escape, MD5, Morse code. 14 tools, no sign-up, works offline.",
        keywords: "base64 encoder online, base64 decoder online, base64 encode decode free, jwt decoder online, jwt token decoder, url encoder decoder online, html entity encoder decoder, html unescape online, hex encoder decoder, hex to text online, unicode escape online, unicode converter, morse code encoder decoder, encoding tools free, decode base64 text",
        faqs: [
          { q: "Can I decode a JWT token online for free?", a: "Yes. The JWT decoder shows the header, payload, all claims, and expiry status directly in your browser. No data is sent to any server." },
          { q: "Is Base64 encoding done client-side?", a: "Yes. All encoding and decoding — Base64, URL, HTML, Hex, Unicode, Morse — runs entirely in your browser using JavaScript. Your data never leaves your device." },
          { q: "Can I encode and decode without an internet connection?", a: "Yes. Once the page is loaded, all 14 encoding tools work fully offline. No server calls are made during encoding or decoding." },
        ],
      },
    ],
  },
  {
    group: "Generators",
    color: "#f59e0b",
    tools: [
      {
        id:    "generators",
        path:  "/generators",
        name:  "Generators",
        icon:  "⚡",
        desc:  "UUID, Password, Lorem Ipsum, QR Code",
        title: "UUID Generator, QR Code, Password & Lorem Ipsum — Free Online | miniDevToolkit",
        meta:  "Free online generators: bulk UUID v4/v1/nil (up to 100), QR code with custom colors, secure password with strength meter and special characters, Lorem Ipsum by word/sentence/paragraph. No sign-up, instant results.",
        keywords: "uuid generator online, uuid v4 generator, random uuid generator, bulk uuid generator, guid generator free, qr code generator free, qr code maker online, create qr code online, custom qr code generator, secure password generator online, random password generator, password generator with special characters, strong password generator, lorem ipsum generator online, placeholder text generator, dummy text generator",
        faqs: [
          { q: "How do I generate a UUID v4 online?", a: "Open the Generators tool, select the UUID tab, and click Generate. You can generate up to 100 UUIDs at once in v4, v1, or nil format with uppercase or braces options." },
          { q: "Is the password generator cryptographically secure?", a: "Yes. Passwords are generated using the browser's built-in crypto.getRandomValues() API, which produces cryptographically secure random values — never predictable." },
          { q: "Can I create a QR code for free with custom colors?", a: "Yes. The QR Code generator supports custom foreground and background colors, four error-correction levels (L/M/Q/H), and downloads as a PNG — all free and client-side." },
        ],
      },
      {
        id:    "fake-data",
        path:  "/fake-data-generator",
        name:  "Fake Data Generator",
        icon:  "⚗",
        desc:  "Realistic test data for forms & APIs",
        title: "Fake Name & Data Generator — Free Dummy & Mock Test Data Online | miniDevToolkit",
        meta:  "Free fake data generator: fake names, random emails, addresses, phone numbers, UUIDs, credit cards, company names. 40+ fields, custom fields, up to 1,000 rows — export as JSON or CSV. No sign-up, 100% browser-based.",
        keywords: "fake name generator, fake data generator online, random name generator, random email generator, fake email generator, dummy data generator, test data generator free, mock data generator online, fake address generator, random address generator, generate fake data csv, generate fake data json, fake user data generator, sample data for testing, fake identities generator, random data generator online",
        faqs: [
          { q: "How many rows of fake data can I generate?", a: "Up to 1,000 rows per export. The preview table shows the first 50 rows to keep the interface responsive, but the full dataset is included in JSON and CSV exports." },
          { q: "Does fake data generation upload data to a server?", a: "No. All data is generated entirely in your browser using JavaScript. Nothing is sent to any server — your test data stays completely private." },
          { q: "Can I add custom fields to the fake data generator?", a: "Yes. Click 'Add Custom Field', name it, and choose a value type: static text, comma-separated list, number range, boolean, incrementing counter, UUID, or date range." },
        ],
      },
    ],
  },
  {
    group: "Security",
    color: "#ef4444",
    tools: [
      {
        id:    "hash-tools",
        path:  "/hash-generator",
        name:  "Hash & Security",
        icon:  "#",
        desc:  "MD5, SHA-256, SHA-512, File Hash",
        title: "Hash Generator — MD5, SHA-256, SHA-512 & File Checksum Free Online | miniDevToolkit",
        meta:  "Free online hash generator: MD5, SHA-256, SHA-512 for text and files. Compute all hashes at once, toggle uppercase, drag-and-drop file hash — no uploads, uses Web Crypto API. No sign-up.",
        keywords: "online hash generator, md5 hash generator online, sha256 hash generator online, sha-256 online, sha512 hash generator, sha512 online, file hash generator, file checksum online, hash value generator, generate md5 checksum, hash text online, hashing tools free, crypto hash tool, all hashes at once, web crypto api hash",
        faqs: [
          { q: "Is SHA-256 hashing done in the browser?", a: "Yes. The hash generator uses the Web Crypto API built into your browser. No data is uploaded to any server — hashing is entirely local." },
          { q: "Can I hash a file without uploading it?", a: "Yes. Drag and drop your file onto the File Hash tab. The hash is computed locally in your browser — the file never leaves your device." },
          { q: "What hashing algorithms are supported?", a: "MD5, SHA-256, and SHA-512 for text input. The 'All Hashes' tab computes all three simultaneously. File hashing supports MD5, SHA-256, and SHA-512." },
        ],
      },
    ],
  },
  {
    group: "Convert",
    color: "#3b8ef8",
    tools: [
      {
        id:    "file-converter",
        path:  "/file-converter",
        name:  "File Converter",
        icon:  "🔄",
        desc:  "PDF↔Word, Images↔PDF, PDF↔PNG",
        title: "PDF to Word, PDF to PNG & Image to PDF Converter Free — No Upload | miniDevToolkit",
        meta:  "Free online file converter — no uploads, no sign-up. PDF to Word (DOCX), Word to PDF, images to PDF, PDF to PNG. Conversor PDF para Word grátis. Converter imagem para PDF. 100% browser-based.",
        keywords: "pdf to word converter free, convert pdf to docx online, free pdf to word no upload, word to pdf converter online, images to pdf converter, jpg to pdf online, png to pdf, pdf to png converter, pdf to images online, convert pdf online free, conversor pdf para word, converter pdf para word gratis, transformar pdf para png, converter imagem para pdf, pdf para png online, compressor pdf gratis, pdf converter browser, free pdf converter no signup",
        faqs: [
          { q: "Does PDF to Word conversion upload my file?", a: "No. All conversions run entirely in your browser using JavaScript libraries (PDF.js, docx.js, jsPDF). Your files never leave your device." },
          { q: "What file formats does the File Converter support?", a: "PDF to Word (DOCX), Word (DOCX) to PDF, JPG/PNG/WebP images to PDF, and PDF to PNG images — all free, browser-based, no sign-up." },
          { q: "Why does the converter need to load external libraries?", a: "PDF and DOCX processing requires large specialist libraries. They are loaded once from a CDN on first use and then cached by your browser for subsequent visits." },
        ],
      },
    ],
  },
  {
    group: "Time & Date",
    color: "#22c55e",
    tools: [
      {
        id:    "time-tools",
        path:  "/time-date-tools",
        name:  "Time & Date",
        icon:  "🕐",
        desc:  "Unix timestamp, Timezone, Cron",
        title: "Unix Timestamp & Epoch Converter — Cron Expression Builder Free Online | miniDevToolkit",
        meta:  "Free Unix timestamp and epoch converter: seconds/ms to date, live mode, full breakdown. Visual cron expression builder with 18 presets and next-5-runs preview. Timezone converter for 35+ zones. No sign-up.",
        keywords: "unix timestamp converter online, epoch converter online, epoch to date converter, convert epoch to date, timestamp to date online, unix time calculator, date to unix timestamp, cron expression generator online, cron time converter, cron job generator online, crontab generator, cron builder online, cron schedule builder, timezone converter online, convert time zones online, time zone calculator",
        faqs: [
          { q: "How do I convert a Unix timestamp to a date?", a: "Paste the timestamp into the Unix Timestamp Converter. It automatically detects seconds vs milliseconds and shows the UTC and local date-time instantly." },
          { q: "What does the cron expression generator do?", a: "It lets you build cron expressions visually using dropdowns, shows a plain-English description of the schedule, previews the next 5 run times, and includes 18 common presets." },
          { q: "How many timezones does the timezone converter support?", a: "35+ timezones covering all major regions. Select a source timezone and time, then see the equivalent time across all zones simultaneously." },
        ],
      },
    ],
  },
  {
    group: "Text",
    color: "#f59e0b",
    tools: [
      {
        id:    "text-tools",
        path:  "/text-utilities",
        name:  "Text Utilities",
        icon:  "✏",
        desc:  "Diff, Duplicates, Case, Regex",
        title: "Remove Duplicate Lines, Text Diff & Regex Tester — Case Converter Free | miniDevToolkit",
        meta:  "Free online text utilities: remove duplicate lines, text diff checker, 12-format case converter (camelCase, snake_case, kebab-case, UPPERCASE…), live regex tester with 10 presets. No sign-up, 100% browser-based.",
        keywords: "remove duplicate lines online, duplicate line remover, deduplicate text online, text diff checker online, compare two texts online, find text differences, case converter online, camelcase converter online, snake case converter, kebab case online, uppercase to lowercase online, convert text to lowercase, text case changer, regex tester online, regular expression tester, live regex tester, test regex pattern online, regex checker free",
        faqs: [
          { q: "Can I compare two texts online for free?", a: "Yes. The Text Diff tool highlights added, removed, and changed lines between two texts in real time — free, private, and runs entirely in your browser." },
          { q: "What case formats does the case converter support?", a: "12 formats simultaneously: UPPERCASE, lowercase, Title Case, Sentence case, camelCase, PascalCase, snake_case, kebab-case, CONSTANT_CASE, dot.case, aLtErNaTiNg, and iNVERT." },
          { q: "Does the regex tester send my patterns to a server?", a: "No. Pattern matching runs entirely in your browser using JavaScript's built-in RegExp engine. Your text and patterns are never uploaded anywhere." },
        ],
      },
    ],
  },
  {
    group: "Compress",
    color: "#a855f7",
    tools: [
      {
        id:    "compression",
        path:  "/compression-toolkit",
        name:  "Compression Toolkit",
        icon:  "🗜",
        desc:  "Image, PDF, ZIP, SVG, CSS/HTML/JSON",
        title: "Image Compressor, Minify CSS & SVG Optimizer — PDF & ZIP Free Online | miniDevToolkit",
        meta:  "Free online compression toolkit: compress JPG/PNG/WebP images, minify CSS/HTML/JSON, optimize SVG files, compress PDFs, create ZIP archives. No uploads, no sign-up, 100% browser-based.",
        keywords: "image compressor online free, compress image free, compress jpg online, compress png online, compress webp, image reducer online, pdf compressor online, compress pdf free online, zip creator online, zip file maker, svg optimizer online, optimize svg file size, css minifier online, minify css online, html minifier online, json minifier online, compress files browser, image size reducer",
        faqs: [
          { q: "Does image compression upload my images?", a: "No. Image compression uses the browser-image-compression library and runs 100% client-side. Your images never leave your device." },
          { q: "What file types can I compress?", a: "JPG, PNG, and WebP images; PDFs; multiple files into a ZIP archive; SVG files; and CSS, HTML, or JSON text for minification — all free and browser-based." },
          { q: "Can I set a maximum file size target for image compression?", a: "Yes. The Max File Size slider overrides the quality slider to guarantee the output stays under your specified size limit, useful for form upload requirements." },
        ],
      },
    ],
  },
];

// FAQ page entry (not a tool — separate page)
export const FAQ_ROUTE = {
  path: "/faq",
  title: "FAQ — Free Developer Tools Questions Answered | miniDevToolkit",
  meta: "Answers to 60+ questions about miniDevToolkit's free developer tools — JSON formatter, Base64 encoder, JWT decoder, UUID generator, fake name generator, hash generator, file converter, regex tester, cron builder, image compressor and more.",
};

// ─── CONTENT PAGES ───────────────────────────────────────────────────────────
// GEO / SEO landing pages — not in sidebar tool list
export const CONTENT_PAGES = [
  {
    id: "free-developer-tools",
    path: "/free-developer-tools",
    title: "25+ Free Online Developer Tools — No Sign-Up | miniDevToolkit",
    meta: "Browse 25+ free browser-based developer tools. JSON formatter, Base64 encoder, UUID generator, hash generator, regex tester, file converter, cron builder and more. No sign-up, no uploads, 100% client-side.",
    keywords: "free developer tools, free online dev tools, browser-based developer utilities, free coding tools no signup, privacy-first dev tools, minidevtoolkit",
    canonical: "https://minidevtoolkit.com/free-developer-tools",
  },
  {
    id: "alt-jsonformatter",
    path: "/alternatives/jsonformatter",
    title: "Free Alternative to JSONFormatter.org — Private JSON Toolkit",
    meta: "miniDevToolkit is a free JSONFormatter.org alternative with JSON diff, CSV/XML/YAML conversion, offline support, and strict client-side privacy. No sign-up, no uploads.",
    keywords: "jsonformatter alternative, free json formatter alternative, json formatter online no signup, private json formatter, json to csv online free, json diff online",
    canonical: "https://minidevtoolkit.com/alternatives/jsonformatter",
  },
  {
    id: "alt-cyberchef",
    path: "/alternatives/cyberchef",
    title: "Lightweight CyberChef Alternative — Free Encoding Tools",
    meta: "miniDevToolkit is a lighter CyberChef alternative for everyday encoding: Base64, URL, JWT, Hex, Unicode, Morse, MD5, SHA-256. No sign-up, works offline, 100% client-side.",
    keywords: "cyberchef alternative, cyberchef online alternative, base64 encoder no signup, encoding tools online free, lightweight cyberchef, jwt decoder online free",
    canonical: "https://minidevtoolkit.com/alternatives/cyberchef",
  },
  {
    id: "alt-dcode",
    path: "/alternatives/dcode",
    title: "Free dcode.fr Alternative — Clean, Ad-Free Dev Utilities",
    meta: "miniDevToolkit is a free dcode.fr alternative: encoding, hash, and text utilities in one privacy-first toolkit. No sign-up, no uploads, browser-based.",
    keywords: "dcode alternative, dcode.fr alternative, encoding tools free online, hash generator no signup, developer utilities free, privacy-first developer tools",
    canonical: "https://minidevtoolkit.com/alternatives/dcode",
  },
  {
    id: "blog-best-tools-2026",
    path: "/blog/best-free-dev-tools-2026",
    title: "25 Best Free Online Developer Tools in 2026",
    meta: "Honest roundup of the 25 best free online developer tools in 2026 — including JSONFormatter, CyberChef, RegExr, Squoosh, Transform.tools, and miniDevToolkit. No sign-up picks highlighted.",
    keywords: "best free developer tools 2026, free online dev tools list, top developer tools no signup, free json formatter 2026, free regex tester 2026, free hash generator 2026",
    canonical: "https://minidevtoolkit.com/blog/best-free-dev-tools-2026",
  },
];

export const ALL_TOOLS = TOOL_GROUPS.flatMap(g => g.tools);

// path → tool lookup (for useLocation matching)
export const PATH_TO_TOOL = Object.fromEntries(
  ALL_TOOLS.map(t => [t.path, t])
);

// ─── RELATED TOOLS MAP ───────────────────────────────────────────────────────
// category: "tech"     → developer-focused tools (APIs, code, security)
// category: "general"  → everyday tools (files, content, media)
// related: up to 4 tool IDs shown as suggestions when user is on that tool

export const TOOL_META = {
  "json-toolkit": {
    category: "tech",
    tags: ["developer", "api", "data", "code"],
    related: ["encoding", "hash-tools", "text-tools", "fake-data"],
    relatedReason: "Developers working with JSON also use these tools",
  },
  "encoding": {
    category: "tech",
    tags: ["developer", "security", "api", "code"],
    related: ["hash-tools", "json-toolkit", "generators", "text-tools"],
    relatedReason: "Commonly used alongside encoding tools",
  },
  "generators": {
    category: "tech",
    tags: ["developer", "api", "testing", "code"],
    related: ["fake-data", "encoding", "hash-tools", "json-toolkit"],
    relatedReason: "Useful for generating test data and secure tokens",
  },
  "fake-data": {
    category: "general",
    tags: ["testing", "content", "mock", "data"],
    related: ["generators", "json-toolkit", "file-converter", "text-tools"],
    relatedReason: "Great for populating forms, mockups and spreadsheets",
  },
  "hash-tools": {
    category: "tech",
    tags: ["developer", "security", "code"],
    related: ["encoding", "json-toolkit", "generators", "text-tools"],
    relatedReason: "Essential security tools for developers",
  },
  "file-converter": {
    category: "general",
    tags: ["files", "documents", "media", "content"],
    related: ["compression", "fake-data", "text-tools", "generators"],
    relatedReason: "Handy tools for working with files and documents",
  },
  "time-tools": {
    category: "tech",
    tags: ["developer", "api", "backend", "code"],
    related: ["json-toolkit", "encoding", "hash-tools", "text-tools"],
    relatedReason: "Useful when debugging APIs and backend services",
  },
  "text-tools": {
    category: "general",
    tags: ["content", "writing", "code", "data"],
    related: ["json-toolkit", "encoding", "fake-data", "compression"],
    relatedReason: "Pair these with your text workflow",
  },
  "compression": {
    category: "general",
    tags: ["files", "media", "images", "content"],
    related: ["file-converter", "fake-data", "text-tools", "generators"],
    relatedReason: "Other tools for handling files and content",
  },
};